/**
 * api/session.ts — typed wrappers over the /v1/auth/web/session endpoints.
 *
 * The web session is the ONE piece of authentication state the SPA
 * cares about. Every other API call is authenticated implicitly by
 * the browser sending the HttpOnly cookie on same-origin XHR.
 *
 * NOTE on CSRF: the server delivers the CSRF token via the
 * `aster_csrf` cookie (readable by JS). The client picks it up per
 * request via `readCsrfFromCookie()` — this file's return types
 * DO NOT include the CSRF secret. Any endpoint that leaks it in JSON
 * fails the regression test in test_web_session_guards.py.
 *
 * Sign-in on web is a two-step phone-OTP flow (migration 013):
 *   1. startPhoneOtp(phone_e164) — sends the SMS.
 *   2. verifyPhoneOtp(challenge_id, code) — returns a verification_token
 *      on success, `wrong_code` / `expired` / `consumed` / `rate_limited`
 *      as terminal states otherwise.
 *   3. createWebSessionFromOtp(verification_token) — burns the token
 *      and sets the two cookies. On a phone that has no user row yet,
 *      returns `signup_required` and the client routes to the signup
 *      surface (once /signup ships) or a coming-soon page.
 */

import { api, ApiClientError } from "./client";

export interface WebSessionOut {
  user_id: string;
  session_kind: "web" | "device";
  /** ISO-8601 of the ABSOLUTE cutoff (issued_at + 8 h). Empty for
   * device sessions. */
  absolute_expires_at: string;
}

/**
 * Exchange a device bearer for a web-session cookie pair. Used only
 * by the dev sign-in flow (`vite dev` builds) — the production entry
 * point is the phone-OTP flow below.
 */
export async function exchangeBearerForCookie(
  bearer: string,
  deviceId: string,
): Promise<WebSessionOut> {
  return api<WebSessionOut>("/v1/auth/web/session", {
    method: "POST",
    bearer,
    deviceId,
  });
}

/**
 * Probe the current session. Returns null if no valid cookie is
 * present. Called once on app load to decide sign-in-first vs.
 * home-first routing.
 */
export async function getCurrentWebSession(): Promise<WebSessionOut | null> {
  try {
    return await api<WebSessionOut>("/v1/auth/web/session", { method: "GET" });
  } catch {
    return null;
  }
}

/**
 * Revoke the current web session. The backend clears BOTH cookies via
 * Set-Cookie Max-Age=0. Client-side we rely on the browser to drop
 * them — no JS cookie manipulation needed.
 */
export async function logoutWebSession(): Promise<void> {
  await api<void>("/v1/auth/web/logout", { method: "POST" });
}

// ---------------------------------------------------------------------------
// Phone OTP flow — the production sign-in path.
//
// These wrap the existing /v1/auth/phone/{start,verify,resend} endpoints
// (mobile has used them since day one) plus the new /v1/auth/web/session/otp
// endpoint that exchanges a verification_token for a web-session cookie
// pair with session_kind='web' and no parent device (see migration 013).
// ---------------------------------------------------------------------------

export interface OtpChallenge {
  challenge_id: string;
  /** How many code attempts are still allowed. Starts at 3. */
  attempts_remaining: number;
  /** ISO-8601 of the challenge's absolute expiry (5 min from issue). */
  expires_at: string;
  /** ISO-8601 of when a `/resend` will succeed. Until this, the button
   * shows a countdown and is disabled. */
  resend_available_at: string;
  phone_e164: string;
  voice_fallback: boolean;
}

/**
 * Kick off an OTP challenge for the phone. Server sends the SMS.
 * Errors surface as thrown `Error` — most commonly `rate_limited`
 * with a `retry_after_seconds` shape.
 */
export async function startPhoneOtp(phoneE164: string): Promise<OtpChallenge> {
  return api<OtpChallenge>("/v1/auth/phone/start", {
    method: "POST",
    body: { phone_e164: phoneE164, intent: "new_device" },
  });
}

/**
 * Resend the OTP. Server invalidates the previous challenge and mints
 * a new one — the client should replace its stored challenge_id with
 * the returned one.
 */
export async function resendPhoneOtp(challengeId: string): Promise<OtpChallenge> {
  return api<OtpChallenge>("/v1/auth/phone/resend", {
    method: "POST",
    body: { challenge_id: challengeId },
  });
}

/** Terminal result kinds from /v1/auth/phone/verify. */
export type PhoneVerifyResult =
  | {
      kind: "success";
      verification_token: string;
      verification_expires_at: string | null;
    }
  | { kind: "wrong_code"; attempts_remaining: number }
  | { kind: "expired" }
  | { kind: "consumed" }
  | { kind: "not_delivered" }
  | { kind: "rate_limited"; retry_after_seconds: number };

export async function verifyPhoneOtp(
  challengeId: string,
  code: string,
): Promise<PhoneVerifyResult> {
  return api<PhoneVerifyResult>("/v1/auth/phone/verify", {
    method: "POST",
    body: { challenge_id: challengeId, code },
  });
}

/**
 * Terminal shapes from /v1/auth/web/session/otp. The endpoint returns
 * a `WebSessionOut` on success and one of the discriminated failure
 * shapes below via a thrown envelope error the caller can switch on:
 *
 *   * `signup_required` — the phone has no user row. Client routes
 *     to /signup (once it ships) or a coming-soon surface.
 *   * `token_invalid` — the verification_token is expired, consumed,
 *     or unknown (server code is `validation_failed`; we rename for
 *     the SPA's UX branching). Caller must restart the phone flow.
 */
export type WebSessionOtpResult =
  | { kind: "success"; session: WebSessionOut }
  | { kind: "signup_required" }
  | { kind: "token_invalid" };

/**
 * Exchange a verification_token for a web-session cookie pair.
 * Uniformly returns a discriminated `WebSessionOtpResult` so callers
 * don't have to catch multiple exception classes.
 */
export async function createWebSessionFromOtp(
  verificationToken: string,
): Promise<WebSessionOtpResult> {
  try {
    const session = await api<WebSessionOut>("/v1/auth/web/session/otp", {
      method: "POST",
      body: { verification_token: verificationToken },
    });
    return { kind: "success", session };
  } catch (e: unknown) {
    // The `api` client surfaces the server's `{ error: { code } }`
    // envelope as an `ApiClientError` with a typed `code` field. We
    // branch on known terminal codes; anything else re-throws so
    // upper layers can render a generic "something went wrong."
    if (e instanceof ApiClientError) {
      if (e.code === "signup_required") return { kind: "signup_required" };
      // `validation_failed` from THIS endpoint unambiguously means
      // the verification_token was expired/consumed/unknown — the
      // only pre-success validation this route does. Restart the
      // phone-OTP flow.
      if (e.code === "validation_failed") return { kind: "token_invalid" };
    }
    throw e;
  }
}

/**
 * Sign-up counterpart. Consumes the verification_token, CREATES a user
 * row if none exists for the phone, and mints a web session. Called
 * from the /signup flow after OTP verification.
 *
 * Idempotent for existing accounts: hitting this endpoint for a phone
 * that already has a user row still returns a session (the client
 * detects existing identity_status and routes accordingly).
 *
 * Different endpoint from /v1/auth/web/session/otp so a signup can't
 * be replayed as a sign-in and vice versa — the caller declares intent
 * by URL.
 */
export type WebSignupResult =
  | { kind: "success"; session: WebSessionOut }
  | { kind: "token_invalid" };

export async function createWebSessionFromSignup(
  verificationToken: string,
): Promise<WebSignupResult> {
  try {
    const session = await api<WebSessionOut>("/v1/auth/web/signup", {
      method: "POST",
      body: { verification_token: verificationToken },
    });
    return { kind: "success", session };
  } catch (e: unknown) {
    if (e instanceof ApiClientError) {
      if (e.code === "validation_failed") return { kind: "token_invalid" };
    }
    throw e;
  }
}
